import { MouseEvent, ReactNode, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { classNames } from "../Utils/classNames.ts";

function colorsOf(isActive: boolean | undefined): string {
  return isActive
    ? "bg-primary text-white hover:brightness-110"
    : "hover:bg-slate-200";
}

function isMovingInto(
  element: HTMLElement | null,
  event: MouseEvent<HTMLElement>,
): boolean {
  return (
    !!element &&
    event.relatedTarget instanceof Node &&
    element.contains(event.relatedTarget)
  );
}

function CollapsedSideMenuButton({
  label,
  icon,
  isActive,
  onClick,
}: {
  label: string;
  icon: ReactNode;
  isActive?: boolean;
  onClick: (event: MouseEvent) => void;
}) {
  const iconButtonRef = useRef<HTMLAnchorElement>(null);
  const expandedButtonRef = useRef<HTMLAnchorElement>(null);
  const [expandedButtonPosition, setExpandedButtonPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  function showExpandedButton() {
    const iconButtonBounds = iconButtonRef.current?.getBoundingClientRect();
    if (!iconButtonBounds) return;
    setExpandedButtonPosition({
      top: iconButtonBounds.top,
      left: iconButtonBounds.left,
    });
  }

  function hideExpandedButton() {
    setExpandedButtonPosition(null);
  }

  return (
    <>
      <a
        ref={iconButtonRef}
        aria-label={label}
        onClick={onClick}
        onMouseEnter={showExpandedButton}
        onMouseLeave={(event) => {
          if (!isMovingInto(expandedButtonRef.current, event))
            hideExpandedButton();
        }}
        className={classNames(
          "w-10 h-10 cursor-pointer rounded-lg flex items-center justify-center",
          colorsOf(isActive),
        )}
      >
        {icon}
      </a>
      {expandedButtonPosition &&
        createPortal(
          <a
            ref={expandedButtonRef}
            onClick={onClick}
            onMouseLeave={(event) => {
              if (!isMovingInto(iconButtonRef.current, event))
                hideExpandedButton();
            }}
            style={expandedButtonPosition}
            className={classNames(
              "fixed z-50 h-10 pl-2.5 pr-4 cursor-pointer rounded-lg flex items-center space-x-2 whitespace-nowrap shadow-lg",
              isActive ? "bg-primary text-white" : "bg-slate-200",
            )}
          >
            {icon}
            <span>{label}</span>
          </a>,
          document.body,
        )}
    </>
  );
}

export function SideMenuButton({
  label,
  to,
  icon,
  isActive,
  onNavigate,
  collapsed,
}: {
  to: string;
  label: string;
  icon: ReactNode;
  isActive?: boolean;
  onNavigate?: () => void;
  collapsed?: boolean;
}) {
  const navigate = useNavigate();

  function goToPage(event: MouseEvent) {
    event.preventDefault();
    navigate(to);
    onNavigate?.();
  }

  if (collapsed) {
    return (
      <CollapsedSideMenuButton
        label={label}
        icon={icon}
        isActive={isActive}
        onClick={goToPage}
      />
    );
  }

  return (
    <a
      onClick={goToPage}
      className={classNames(
        "px-4 cursor-pointer py-2 shadow-slate-200/40 rounded-lg w-full text-center flex items-center space-x-2",
        colorsOf(isActive),
      )}
    >
      {icon} <span>{label}</span>
    </a>
  );
}
